import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, lazy } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, isDemoPath } from './hooks/useAuth';
import { useUserPreferences } from './hooks/useUserPreferences';
import { usePortfolio } from './hooks/usePortfolio';
import { clearTokenCache } from './tokens';
import { useTimeline } from './hooks/useTimeline';
import { useDemoData } from './hooks/useDemoData'; 
import { useSnapshots } from './hooks/useSnapshots';
import { getEffectiveSnapshotDate, parseDateInputToLocalDate } from './utils/snapshots';
import { calculateAllAnalytics, calculateTotalAnnualIncome, calculateItemAnnualAppreciationValue, calculateRealEstateReturnOnEquity } from './logic/analytics';
import { itemMonthlyCashflow } from './logic/engine';
import { generateProjectionData } from './logic/projection';
import { buildEstimates, buildReviewList, DEFAULT_STALENESS_THRESHOLD } from './logic/review';
import { computeAwayDelta, shouldShowAwayDelta } from './logic/awayDelta';
import { useLastSeenAt } from './hooks/useLastSeenAt';
import { computeFireMetrics } from './logic/fire';
import { generateSankeyData } from './logic/sankey';

import useMediaQuery from './hooks/useMediaQuery';
import useHistoryBack, { useBackTrap } from './hooks/useHistoryBack';
import Toast from './components/Toast';
import BottomNav from './components/BottomNav';
import AppBackground from './components/AppBackground';
import MobileTopBar from './components/MobileTopBar';
import AppLogo from './components/AppLogo';
import AppFooter from './components/AppFooter';
import DemoBanner from './components/DemoBanner';
import SideDrawer from './components/SideDrawer';
import { ExplainProvider } from './components/explain/ExplainContext';
import { resetAccountData } from './logic/accountReset';
import { db, appId, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { STRIPE_PORTAL_URL } from './config/billing';
import { usePlan } from './hooks/usePlan';
import { trackEvent } from './hooks/useConsent';
import { FREE_LIMITS, consumePostAuthPaywall } from './config/billing';
import PaywallModal from './components/PaywallModal';
import { CheckCircle2 } from 'lucide-react';
import ItemForm from './components/ItemForm';
import ModalDrawer from './components/ModalDrawer.js';
import { errorMessage } from './helpers';
import DashboardCustomizationModal from './components/DashboardCustomizationModal';
import LocalizationModal from './components/LocalizationModal';
import BugReportModal from './components/BugReportModal';
import SnapshotModal from './components/SnapshotModal';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';

// H3 — code splitting. The build was one 512 kB gzipped bundle: a visitor
// arriving on the landing page from a phone downloaded and parsed Firebase,
// Recharts, Chart.js, all eight pages and the whole projection engine before
// the hero could render. That is what paid creator traffic was spending its
// first two seconds on.
//
// Three seams, all of them real boundaries a user crosses rather than arbitrary
// cut points:
//   1. the three landing pages, which a signed-in user never sees, and which are
//      the ONLY thing a signed-out visitor needs;
//   2. SimulationPage, sole consumer of Chart.js — a second charting library
//      that now never reaches anyone who does not open Monte Carlo;
//   3. the remaining pages, one chunk each, fetched when navigated to.
//
// Every one of these is behind a Suspense boundary AND a ChunkErrorBoundary;
// see that file for why the second one is not optional once you do this.
const LandingPage = lazy(() => import('./components/LandingPage'));
const FireLandingPage = lazy(() => import('./components/FireLandingPage'));
const PlannerLandingPage = lazy(() => import('./components/PlannerLandingPage'));

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const TimelinePage = lazy(() => import('./pages/TimelinePage'));
const SimulationPage = lazy(() => import('./pages/SimulationPage'));
const FirePage = lazy(() => import('./pages/FirePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PlanPage = lazy(() => import('./pages/PlanPage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const OnboardingFlow = lazy(() => import('./components/onboarding/OnboardingFlow'));

// NOT split, and this was tried: ItemForm and the five modals above. They are
// ~143 kB of source that only a signed-in user can open, so they look like an
// easy fourth seam — but `SnapshotModal` and `DashboardCustomizationModal` are
// mounted unconditionally and toggled by `isOpen` (so ModalDrawer's slide-out
// close animation can play), which means lazy versions would suspend the shell
// on every app entry and flash the boot screen. The conditional ones would need
// their own null-fallback Suspense to avoid the same flash when opened, and
// ItemForm takes a ref. Perhaps 30-40 kB gzipped, for four ways to make the app
// visibly worse. The three seams above are where the value was.

// A snapshot is a historical record — nothing in it is ever out of date, so the
// review page has nothing to say. Shared instance so the memo below stays stable.
const EMPTY_REVIEW_LIST = Object.freeze({ needsReview: [], updatedToday: [], calculated: [] });

// The boot screen: the mark, a spinner and one line. It used to be a 128px ring
// drawn from four inline-styled divs and an inlined @keyframes block; the demo's
// `.boot` is the same thing said once, in the stylesheet.
//
// Module scope, not inside App. Declared in the component body it was a fresh
// component type on every render, which React treats as a different component:
// it unmounted the old one, mounted a new one, and the spinner's animation
// restarted from frame zero each time. H3 made this matter more, since the
// screen is now shown at real boundaries rather than only during auth.
const LoadingSpinner = ({ text }) => (
    <div className="boot open">
        <AppLogo />
        <span className="spinner lg" />
        <p>{text}</p>
    </div>
);

// Signals that the first page has painted. Rendered next to the page inside the
// content Suspense, so its effect only runs once the page chunk has resolved and
// mounted; the extra frame lets the browser paint it before the cover lifts.
const PageReady = ({ onReady }) => {
    useEffect(() => {
        const id = requestAnimationFrame(() => onReady());
        return () => cancelAnimationFrame(id);
    }, [onReady]);
    return null;
};

// The in-shell fallback for a page chunk. The nav, the background and the top
// bar stay exactly where they are and only the content area waits — a
// full-screen boot flash for a 30 ms fetch reads as the app breaking, not
// loading.
//
// It says what it is doing. A bare spinner floating in a content column reads
// as something stuck rather than something loading, which is the opposite of
// what a fallback is for; the boot screen already pairs its spinner with a word
// and this is the same moment, one layer in.
const PageFallback = ({ text }) => (
    <div className="page-fallback" role="status" aria-busy="true">
        <span className="spinner lg" />
        <p>{text}</p>
    </div>
);

function App() {
  const { t, i18n } = useTranslation();

  const [currentPage, setCurrentPage] = useState('dashboard');
  const [exitToastVisible, setExitToastVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isItemFormOpen, setIsItemFormOpen] = useState(false);
  const itemFormRef = useRef(null);
  // a second tap on Save while the first write is in flight would add the item twice
  const [isSavingItem, setIsSavingItem] = useState(false);
  const [projectionYears, setProjectionYears] = useState(10);
  
  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem('theme_v10');
    return storedTheme === 'light' ? 'light' : 'dark';
  });
  
  const isMobile = useMediaQuery('(max-width: 768px)');
  // The shell switches at 900px — the Variant C breakpoint where the sidebar
  // gives way to the top bar + tab bar (responsive.css, and SideDrawer's own
  // query). `isMobile` stays at 768px for phone-only concerns (page layout,
  // the vertical flow map); gating the shell with it left the 769–900px band
  // with no nav at all and a permanently open side panel.
  const isShellMobile = useMediaQuery('(max-width: 900px)');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
  const [isLocalizationModalOpen, setIsLocalizationModalOpen] = useState(false);
  const [isBugReportModalOpen, setIsBugReportModalOpen] = useState(false);
  const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
  const [currentSnapshotId, setCurrentSnapshotId] = useState(null);

  const [modalConfig, setModalConfig] = useState({ isOpen: false, message: '', isConfirm: false, onConfirm: null, inputs: [], inputValues: {} });
  const [modalInputValues, setModalInputValues] = useState({});
  // `danger`: the confirm destroys something, so its button says so in red.
  const showModal = useCallback((message, isConfirm = false, onConfirmCallback = null, inputOptions = null, modalChildren = null, confirmLabel = 'general.confirm', cancelLabel = 'general.cancel', danger = false) => {
    const normalizedInputs=Array.isArray(inputOptions)?inputOptions:(inputOptions?[inputOptions]:[]);
    const initialValues = {};
    normalizedInputs.forEach(inputOpt => {
      initialValues[inputOpt.name] = inputOpt.initialValue !== undefined ? inputOpt.initialValue : '';
    });
    setModalInputValues(initialValues);
    setModalConfig({isOpen:true,message,isConfirm,onConfirm:onConfirmCallback,inputs:normalizedInputs, children: modalChildren, confirmLabel, cancelLabel, danger, error: null});
  },[]);
  // Only flips isOpen: the drawer keeps showing its content while it slides out.
  const closeModal = useCallback(() => {
    setModalConfig(c => ({ ...c, isOpen: false, onConfirm: null }));
  }, []);

  const { currentUser, userId, isAuthReady, isDemoMode, handleEnterDemoMode, handleSignOut: signOutLogic } = useAuth(t);

  // Freemium plan state (live listener on the webhook-written billing doc)
  // + the single reusable paywall.
  const { isPro, planReady, currentPeriodEnd, cancelAtPeriodEnd, cancelAt, priceInterval, discountCode, discountDuration, billingStatus } = usePlan(userId, isDemoMode);
  const [paywallSource, setPaywallSource] = useState(null);
  const openPaywall = useCallback((source) => {
    // Compare/Export are teasers for unbuilt features — a Pro user clicking
    // them shouldn't see a paywall for something they already paid for.
    if (isPro && (source === 'compare' || source === 'export')) {
      // Own event, not gate_hit: a Pro click is demand for the feature, not a
      // conversion-funnel step. Reuses the registered `gate` dimension so the
      // build-order ranking = gate_hit + pro_teaser_click, grouped by gate.
      trackEvent('pro_teaser_click', { gate: source });
      showModal(t('billing.paywall.proComingSoon'));
      return;
    }
    if (source === 'compare' || source === 'export') {
      trackEvent('gate_hit', { gate: source });
    }
    trackEvent('paywall_open', { source });
    setPaywallSource(source);
  }, [isPro, showModal, t]);
  const closePaywall = useCallback(() => setPaywallSource(null), []);
  const handleSnapshotGate = useCallback((gate) => {
    trackEvent('gate_hit', { gate });
    openPaywall(gate);
  }, [openPaywall]);
  const snapshotPlanGate = useMemo(() => ({ isPro, onGateHit: handleSnapshotGate }), [isPro, handleSnapshotGate]);

  // "Manage / cancel subscription": mint an authenticated Stripe portal
  // session for this user and redirect there (no second email login). Falls
  // back to the no-code portal login link if the callable is unavailable.
  const handleManageBilling = useCallback(async () => {
    try {
      const res = await httpsCallable(functions, 'createPortalSession')({ returnUrl: window.location.href });
      const url = res && res.data && res.data.url;
      if (url) {
        // Portal changes (cancel, card, plan) come back via webhook — but flag
        // the return so we also pull Stripe's truth directly on reload, in case
        // an event is delayed or missed.
        try { localStorage.setItem('fiwara_billingReturn', '1'); } catch {}
        window.location.assign(url);
        return;
      }
      throw new Error('portal session returned no url');
    } catch (error) {
      console.error('createPortalSession failed:', error);
      if (STRIPE_PORTAL_URL) {
        window.open(STRIPE_PORTAL_URL, '_blank', 'noopener,noreferrer');
        return;
      }
      showModal(t('billing.settings.portalError'));
    }
  }, [showModal, t]);

  // Returning from Stripe checkout (?checkout=success): strip the param and
  // show the "activating…" dialog until the webhook flips the plan.
  const [checkoutReturn, setCheckoutReturn] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('checkout') !== 'success') return false;
      params.delete('checkout');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
      return true;
    } catch {
      return false;
    }
  });
  const checkoutReturnTracked = useRef(false);
  useEffect(() => {
    if (checkoutReturn && !checkoutReturnTracked.current) {
      checkoutReturnTracked.current = true;
      trackEvent('checkout_success_return');
    }
  }, [checkoutReturn]);

  // Self-heal: after returning from checkout or the customer portal, pull the
  // live subscription from Stripe and rewrite the billing doc, so the app
  // converges on Stripe's truth even if a webhook event was delayed or missed.
  const billingRefreshDone = useRef(false);
  useEffect(() => {
    if (isDemoMode || !userId || billingRefreshDone.current) return;
    let portalReturn = false;
    try {
      if (localStorage.getItem('fiwara_billingReturn')) {
        localStorage.removeItem('fiwara_billingReturn');
        portalReturn = true;
      }
    } catch {}
    if (!checkoutReturn && !portalReturn) return;
    billingRefreshDone.current = true;

    // L4 — a bounded poll rather than a single call.
    //
    // The webhook and this call race, and the webhook usually wins; when it
    // does, `isPro` flips and the schedule below stops on its own. When it does
    // NOT — the endpoint is down, or its signing secret is stale — this is the
    // only thing standing between a paying customer and a Free account. One
    // call was not enough: the customer may not exist in Firestore at the
    // instant the page loads, since it is the webhook that creates it.
    //
    // Three attempts over ~30 s, then stop and let the copy take over
    // (`billing.checkout.activatingHint` already gives the user an email
    // address). Polling past that would not be self-healing, it would be
    // waiting for something that is not coming.
    let cancelled = false;
    const timers = [];
    const attempt = () => {
      if (cancelled) return;
      httpsCallable(functions, 'refreshSubscription')()
        .then((res) => {
          // Synced to a real plan — the listener on the billing doc takes it
          // from here, so there is nothing left to poll for.
          if (res?.data?.plan === 'pro') cancelled = true;
        })
        .catch((err) => { console.error('refreshSubscription failed:', err); });
    };
    attempt();
    [5000, 15000, 30000].forEach((delay) => { timers.push(setTimeout(attempt, delay)); });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [userId, isDemoMode, checkoutReturn]);

  // Annotation pass 4: the demo opens on the five-year projection rather than
  // the ten. Ten years of a stranger's numbers is a long chart to read before
  // deciding whether the app is for you, and five is where the timeline events
  // in the demo portfolio actually happen. Signed-in accounts keep the ten-year
  // default, and this only fires on the transition into demo mode, so a visitor
  // who picks another period keeps it.
  useEffect(() => {
    if (isDemoMode) setProjectionYears(5);
  }, [isDemoMode]);

  // plan_activated: fires when the plan flips free→pro during this session
  // (the webhook landing). Outside the checkout-return flow it also shows a
  // small congrats notice.
  const prevIsProRef = useRef(null);
  useEffect(() => { prevIsProRef.current = null; }, [userId]);
  useEffect(() => {
    if (!planReady) return;
    const prev = prevIsProRef.current;
    prevIsProRef.current = isPro;
    if (prev === false && isPro) {
      trackEvent('plan_activated');
      if (!checkoutReturn) showModal(t('billing.congrats'));
    }
  }, [isPro, planReady, checkoutReturn, showModal, t]);

  // Initialize user preferences first (without snapshot dependencies initially)
  const { liquidAssets: liquidAssetsInitial, liquidAssetsInterestRate: liquidAssetsInterestRateInitial, liquidAssetsAsOf, dashboardSettings, handleUpdateDashboardSettings, handleUpdateLiquidAssets: handleUpdateLiquidAssetsBase, confirmLiquidValue, uiPreferences } = useUserPreferences(userId, isDemoMode, showModal, t, currentSnapshotId, null, null);
  
  // Initialize demo data with currency from preferences
  // D2 — `sandbox` tells useDemoData that these figures are editable, so it
  // seeds them once instead of rebuilding them from constants.js every time the
  // language or currency changes. The landing pages leave it off.
  const { demoItems, setDemoItems, demoTimelineEvents, setDemoTimelineEvents, demoSnapshotHistory } = useDemoData(uiPreferences.currency, liquidAssetsInitial, { sandbox: isDemoMode });
  
  // Memoize demoSeed to prevent infinite loops - only recreate when dependencies actually change
  const demoSeed = useMemo(() => {
    if (!isDemoMode) return null;
    return {
      items: demoItems,
      timelineEvents: demoTimelineEvents,
      liquidAssets: liquidAssetsInitial,
      liquidAssetsInterestRate: liquidAssetsInterestRateInitial,
      locale: i18n.language,
      // D2 — which currency the seed's figures are expressed in, so
      // useSnapshots can re-express its own copies when the visitor switches
      // instead of rebuilding them (which would discard a snapshot they made).
      currency: uiPreferences.currency,
      // What each seeded snapshot holds. Without it every snapshot carries
      // today's portfolio and the history line is flat — annotation pass 4.
      history: demoSnapshotHistory,
    };
  }, [isDemoMode, demoItems, demoTimelineEvents, demoSnapshotHistory, liquidAssetsInitial, liquidAssetsInterestRateInitial, i18n.language, uiPreferences.currency]);
  
  // Initialize snapshots with demo data (if in demo mode)
  const { snapshots, loading: snapshotsLoading, createSnapshot, updateSnapshot, deleteSnapshot } = useSnapshots(userId, isDemoMode, showModal, t, demoSeed, snapshotPlanGate);
  
  const activeSnapshot = currentSnapshotId ? snapshots.find(s => s.id === currentSnapshotId) : null;
  
  // Get final user preferences with snapshot dependencies
  // Use a ref to store updateSnapshot so we can use it in the wrapper
  const updateSnapshotRef = useRef(updateSnapshot);
  useEffect(() => { updateSnapshotRef.current = updateSnapshot; }, [updateSnapshot]);
  
  // Use the initial values, but override with snapshot values if a snapshot is active
  const liquidAssets = useMemo(() => {
    return activeSnapshot ? (activeSnapshot.liquidAssets ?? liquidAssetsInitial) : liquidAssetsInitial;
  }, [activeSnapshot, liquidAssetsInitial]);
  
  const liquidAssetsInterestRate = useMemo(() => {
    return activeSnapshot ? (activeSnapshot.liquidAssetsInterestRate ?? liquidAssetsInterestRateInitial) : liquidAssetsInterestRateInitial;
  }, [activeSnapshot, liquidAssetsInterestRateInitial]);
  
  const handleUpdateLiquidAssets = useCallback(async (values) => {
    const parsedAmount = parseFloat(values.liquidAssetsAmount);
    const parsedRate = parseFloat(values.liquidAssetsInterestRate || 0) / 100;
    if (isNaN(parsedAmount) || isNaN(parsedRate) || parsedRate < 0) {
      showModal(t('warnings.invalidNumber'));
      return;
    }
    // D2 — everything else in the demo is editable now, but a snapshot is not:
    // writing into one is the single demo action that would need a persisted
    // record to make sense. Caught here rather than in `updateSnapshot`, so
    // that renaming and re-dating a demo snapshot still work.
    if (isDemoMode && currentSnapshotId) {
      showModal(t('warnings.demoSnapshotReadOnly'));
      return;
    }
    if (currentSnapshotId && updateSnapshotRef.current) {
      updateSnapshotRef.current(currentSnapshotId, { liquidAssets: parsedAmount, liquidAssetsInterestRate: parsedRate });
      return;
    }
    await handleUpdateLiquidAssetsBase(values);
  }, [currentSnapshotId, isDemoMode, handleUpdateLiquidAssetsBase, showModal, t]);

  const { items, loading, handleAddItem, handleUpdateItem, handleDeleteItem, handleItemActiveToggle, handleItemFireToggle, handleBulkAction, confirmItemValue } = usePortfolio(userId, isDemoMode, showModal, { demoItems, setDemoItems }, t, currentSnapshotId, activeSnapshot, updateSnapshot);
  const { timelineEvents, loading: timelineLoading, addTimelineEvent, updateTimelineEvent, deleteTimelineEvent, toggleTimelineEventDisabled } = useTimeline(userId, isDemoMode, showModal, { demoTimelineEvents, setDemoTimelineEvents }, t, currentSnapshotId, activeSnapshot, updateSnapshot);
  
  const handleSetCurrentPage = useCallback((page) => {
      if (isMobile) {
          uiPreferences.setScrollPositions(prev => ({ ...prev, [currentPage]: window.scrollY }));
      }
      setCurrentPage(page);
  }, [isMobile, currentPage, uiPreferences]);

  // System back button (mobile): peel one layer toward the dashboard per press.
  // useBackTrap installs a base entry so back on the dashboard with nothing open
  // stays in the app; the 'page' layer below sends back to the dashboard from any
  // other page. Open modals push their own layers on top via their own hooks, so
  // they always close first. Keep useBackTrap ABOVE the page hook (stack order).
  const goToDashboard = useCallback(() => handleSetCurrentPage('dashboard'), [handleSetCurrentPage]);
  const exitToastTimer = useRef(null);
  const showExitPrompt = useCallback(() => {
      setExitToastVisible(true);
      if (exitToastTimer.current) clearTimeout(exitToastTimer.current);
      exitToastTimer.current = setTimeout(() => setExitToastVisible(false), 2000);
  }, []);
  useBackTrap(isMobile, showExitPrompt);
  useHistoryBack(currentPage !== 'dashboard' && isMobile, goToDashboard, true, 'page');

  useEffect(() => {
    if (isMobile) {
        window.scrollTo({ top: uiPreferences.scrollPositions[currentPage] || 0, behavior: 'instant' });
    }
  }, [currentPage, isMobile, uiPreferences.scrollPositions]);

  useEffect(() => {
      setCurrentSnapshotId(null);
      // Entering/leaving demo or logging in swaps the whole view (landing ⇄ app);
      // don't inherit the landing page's scroll position. 'instant' bypasses the
      // global `scroll-behavior: smooth`, so there's no visible scroll animation.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [userId, isDemoMode]);

  // Everything that is not React state happens here, synchronously, *before*
  // the re-render — not inside the updater and not in the effect below.
  //
  // The effect runs after commit, which is one render too late for anything
  // that reads a token during render: the simulation's chart memo depends on
  // `theme`, so it re-reads on the very render this toggle causes, and an
  // invalidation that lands afterwards leaves the canvas a theme behind until
  // some unrelated render happens to re-read. (`token()` is keyed by theme now
  // as well, so this is two answers to one bug — but the ordering is the one
  // that makes the attribute true when the render reads it.)
  //
  // Out of the updater because an updater has to be pure: React may call it
  // twice, and writing to localStorage and the DOM from inside it is a smell
  // even when it happens to work.
  const toggleTheme = () => {
      const newTheme = theme === 'light' ? 'dark' : 'light';
      const apply = () => {
          localStorage.setItem('theme_v10', newTheme);
          document.documentElement.setAttribute('data-theme', newTheme);
          clearTokenCache();
          flushSync(() => setTheme(newTheme));
      };
      // Cross-fade instead of flipping the whole screen's brightness in one
      // frame; browsers without View Transitions just switch.
      if (document.startViewTransition) document.startViewTransition(apply);
      else apply();
  };

  useEffect(() => {
      const root = document.documentElement;
      root.setAttribute('data-theme', theme);
      // index.html paints the ground as an *inline* style before the stylesheet
      // lands, so a light-theme user never sees a dark frame. An inline style
      // beats every rule in canvas.css, so it has to be re-written here too:
      // without this the attribute flipped, the tokens flipped, and the page
      // behind the cards stayed whatever colour boot had painted it.
      // Read back from the token so there is one definition of each ground.
      const ground = getComputedStyle(root).getPropertyValue('--bg').trim();
      if (ground) root.style.backgroundColor = ground;
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', ground || (theme === 'dark' ? '#0b0f1e' : '#eef1f8'));
      // Belt and braces. `toggleTheme` already cleared this before the render
      // that reads it, and `token()` is keyed by theme besides — but this effect
      // is also what runs on the *first* mount, where boot set the attribute and
      // nothing has cleared anything.
      clearTokenCache();
  }, [theme]);

  // M12 — `public/index.html` hardcodes `lang="en"`, so a Czech session was
  // shipping Czech text inside an English document: wrong voice for screen
  // readers, wrong hyphenation, wrong answer for translation prompts.
  // `i18n.language` can carry a region ("cs-CZ"), which is a valid BCP 47 tag,
  // so it is used as-is.
  useEffect(() => {
      document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const navigateToAddItem = useCallback(() => { setEditingItem(null); setIsItemFormOpen(true); }, []);
  const navigateToEditItem = useCallback((item) => { setEditingItem(item); setIsItemFormOpen(true); }, []);
  const handleCancelForm = useCallback(() => { setIsItemFormOpen(false); setEditingItem(null); }, []);

  const handleSignOut = useCallback(() => {
      if (isDrawerOpen) setIsDrawerOpen(false);
      signOutLogic(showModal);
  },[isDrawerOpen, signOutLogic, showModal]);

  const handleDemoMode = useCallback(() => {
      trackEvent('demo_start');
      handleEnterDemoMode();
  }, [handleEnterDemoMode]);

  // Arrived through the direct /demo link (useAuth starts in demo mode).
  useEffect(() => {
      if (isDemoMode && isDemoPath()) trackEvent('demo_start', { source: 'link' });
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Activation: the first time this user's own plan has real data on this
  // device (localStorage keeps the event once-per-user).
  useEffect(() => {
      if (isDemoMode || !userId || loading || items.length === 0) return;
      const key = `fiwara_activated_${userId}`;
      try {
          if (!localStorage.getItem(key)) {
              localStorage.setItem(key, '1');
              trackEvent('activation');
          }
      } catch { /* best-effort analytics */ }
  }, [isDemoMode, userId, loading, items.length]);

  // When set, the landing page auto-opens its auth modal in this mode on mount.
  // Used by the demo-mode CTA to drop the user on the marketing page with the
  // registration form already open.
  const [landingAuthMode, setLandingAuthMode] = useState(null);
  const handleCreateAccountFromDemo = useCallback(() => {
      setLandingAuthMode('signup');
      if (isDrawerOpen) setIsDrawerOpen(false);
      signOutLogic(showModal); // leaves demo → App renders the landing page
  }, [isDrawerOpen, signOutLogic, showModal]);

  // Checkout return in a signed-out browser: open the login form so the
  // "activating…" dialog can take over right after sign-in.
  useEffect(() => {
      if (checkoutReturn && isAuthReady && !currentUser && !isDemoMode) {
          setLandingAuthMode('login');
      }
  }, [checkoutReturn, isAuthReady, currentUser, isDemoMode]);

  // Post-signup continuation: reopen the paywall a visitor asked for on the
  // landing pricing card / in demo, once the account reaches its dashboard
  // (i.e. after onboarding, so we never interrupt it).
  useEffect(() => {
      if (isDemoMode || !currentUser || !planReady || isPro) return;
      if (dashboardSettings.onboardingStatus === 'pending') return;
      const source = consumePostAuthPaywall();
      if (source) openPaywall(source);
  }, [isDemoMode, currentUser, planReady, isPro, dashboardSettings.onboardingStatus, openPaywall]);

  const handleModalInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setModalInputValues(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  // D2 — this used to refuse EVERY confirmation dialog in demo mode except the
  // cash one, which meant the delete and bulk-action confirmations could never
  // reach their handlers. With the demo writing to its own in-memory store,
  // that blanket refusal is what stood between the visitor and the real
  // workflow, so it is gone. The two genuinely account-level actions — reset
  // and delete account — are disabled at the button in demo (SettingsPage), so
  // nothing dangerous is reachable from here.
  // An onConfirm that returns `false` keeps the modal open — it has put an
  // error in modalConfig.error for the user to fix in place.
  const confirmAction = useCallback(() => {
    let result;
    if (modalConfig.onConfirm) {
      result = modalConfig.inputs && modalConfig.inputs.length > 0
        ? modalConfig.onConfirm(modalInputValues)
        : modalConfig.onConfirm();
    }
    if (result !== false) closeModal();
  }, [modalConfig, modalInputValues, closeModal]);
  const setModalError = useCallback((error) => setModalConfig(c => ({ ...c, error })), []);

  const onEditLiquidAssets = useCallback(() => {
    const inputs = [
        {type:'number', name:'liquidAssetsAmount', label:t('itemForm.realEstate.currentValue'), initialValue:liquidAssets},
        {type:'percentage', name:'liquidAssetsInterestRate', label:`${t('dashboard.interestRate')} (%)`, initialValue:(liquidAssetsInterestRate*100).toFixed(2).replace(/\.00$/,'')}
    ];
    showModal(t('dashboard.editLiquidAssets'), true, handleUpdateLiquidAssets, inputs, null, "general.save");
  }, [showModal, handleUpdateLiquidAssets, liquidAssets, liquidAssetsInterestRate, t]);
  
  const activeItems = useMemo(() => items.filter(item => item.isActive !== false), [items]);
  
  const { netWorth, grossWorth, totalMonthlyCashflow, totalAnnualAppreciation, effectivePortfolioYield, equityRatio } = useMemo(
      () => calculateAllAnalytics(activeItems, liquidAssets, liquidAssetsInterestRate),
      [activeItems, liquidAssets, liquidAssetsInterestRate]
  );
  
  const totalAnnualIncome = useMemo(
      () => calculateTotalAnnualIncome(activeItems, liquidAssets, liquidAssetsInterestRate),
      [activeItems, liquidAssets, liquidAssetsInterestRate]
  );

  // F28 — "Keep your plan fresh". Rolling a value forward produces a *estimate*,
  // never a silent change: every number on every page stays the one the user
  // confirmed until they accept an update here. `evaluatedAt` is pinned for the
  // session so the list can't shuffle under the user mid-review.
  const [evaluatedAt] = useState(() => new Date());
  const [isReviewBannerDismissed, setIsReviewBannerDismissed] = useState(false);

  const portfolioMonthlyCashflow = useMemo(
      () => activeItems.reduce((sum, item) => sum + itemMonthlyCashflow(item), 0),
      [activeItems]
  );

  // Snapshots are historical records — nothing in them is ever "out of date".
  const estimates = useMemo(() => (currentSnapshotId ? [] : buildEstimates({
      items: activeItems,
      liquidAssets,
      liquidAssetsInterestRate,
      liquidAssetsAsOf,
      monthlyNetCashflow: portfolioMonthlyCashflow,
      threshold: dashboardSettings.valueStalenessThreshold || DEFAULT_STALENESS_THRESHOLD,
      now: evaluatedAt,
  })), [currentSnapshotId, activeItems, liquidAssets, liquidAssetsInterestRate, liquidAssetsAsOf, portfolioMonthlyCashflow, dashboardSettings.valueStalenessThreshold, evaluatedAt]);

  const estimatesByItemId = useMemo(() => {
      const map = {};
      estimates.forEach(s => { if (s.itemId) map[s.itemId] = s; });
      return map;
  }, [estimates]);

  // F28.1 — the review page lists everything with a confirmable value, not just
  // what we can estimate. Estimates above stay exactly as they were: the
  // dashboard banner and the orange card hints keep counting estimates only,
  // because the review page is opt-in and the banner is not.
  const reviewList = useMemo(() => (currentSnapshotId ? EMPTY_REVIEW_LIST : buildReviewList({
      items: activeItems,
      liquidAssets,
      liquidAssetsInterestRate,
      liquidAssetsAsOf,
      monthlyNetCashflow: portfolioMonthlyCashflow,
      threshold: dashboardSettings.valueStalenessThreshold || DEFAULT_STALENESS_THRESHOLD,
      now: evaluatedAt,
  })), [currentSnapshotId, activeItems, liquidAssets, liquidAssetsInterestRate, liquidAssetsAsOf, portfolioMonthlyCashflow, dashboardSettings.valueStalenessThreshold, evaluatedAt]);

  // F29 — "Since your last visit". Shares arithmetic with F28 and nothing else:
  // this window starts when the user was last here, not when a value was last
  // confirmed, and it reads no F28 field. Reading `lastSeenAt` also advances it,
  // which is why it happens once per session rather than per render.
  const [isAwayBannerDismissed, setIsAwayBannerDismissed] = useState(false);
  const lastSeenAt = useLastSeenAt(userId, isDemoMode, evaluatedAt);

  // Suppressed inside a snapshot: a snapshot is a historical record, not a visit.
  // Cashflow is derived inside, not passed in: F29 needs it with debt service
  // removed (repaying principal is a transfer, not a gain), which is a different
  // figure from the `portfolioMonthlyCashflow` F28 wants.
  const awayDelta = useMemo(() => (currentSnapshotId || !lastSeenAt ? null : computeAwayDelta({
      items: activeItems,
      liquidAssets,
      liquidAssetsInterestRate,
      // The denominator the dashboard displays, so the percentage here and the
      // figure on the net worth card can never disagree.
      currentNetWorth: netWorth + liquidAssets,
      lastSeenAt,
      now: evaluatedAt,
  })), [currentSnapshotId, lastSeenAt, activeItems, liquidAssets, liquidAssetsInterestRate, netWorth, evaluatedAt]);

  const showAwayDelta = !isAwayBannerDismissed && shouldShowAwayDelta(awayDelta);

  useEffect(() => {
      if (!showAwayDelta) return;
      trackEvent('away_delta_shown', {
          delta: Math.round(awayDelta.total),
          days: Math.round(awayDelta.days),
          currency: uiPreferences.currency,
      });
      // Once per session: the delta is computed from a clock pinned at load, so
      // this can only fire again after a reload — which is a new visit.
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAwayDelta]);

  // The review page settles a row two ways, both the same write with a
  // different number: Apply writes what's in the row's field, Still correct
  // passes null so only the confirmation date moves.
  const settleReviewRow = useCallback((row, newValue) => {
      if (row.kind === 'liquid') confirmLiquidValue(row, newValue);
      else confirmItemValue(row, newValue);
  }, [confirmItemValue, confirmLiquidValue]);

  const handleApplyValue = useCallback((row, newValue) => {
      // The estimate untouched is a different act from a number the user typed
      // over it, and the two answer different questions about the feature.
      const tookEstimate = newValue === row.estimate?.estimatedValue;
      trackEvent(tookEstimate ? 'estimate_applied' : 'value_overridden', {
          kind: row.kind,
          monthsStale: Math.round(row.monthsStale),
          hadEstimate: !!row.estimate,
      });
      settleReviewRow(row, newValue);
  }, [settleReviewRow]);

  const handleConfirmValue = useCallback((row) => settleReviewRow(row, null), [settleReviewRow]);

  const openReviewPage = useCallback(() => {
      trackEvent('review_page_open');
      handleSetCurrentPage('review');
  }, [handleSetCurrentPage]);

  const projectionData = useMemo(() => generateProjectionData({
      basePortfolioItems: activeItems, 
      liquidAssets, 
      liquidAssetsInterestRate, 
      projectionYears, 
      dashboardSettings,
      timelineEvents: dashboardSettings.isScenarioActive ? timelineEvents : [],
      currency: uiPreferences.currency,
      locale: i18n.language,
      nowLabel: t('general.now')
  }), [activeItems, liquidAssets, liquidAssetsInterestRate, projectionYears, dashboardSettings, timelineEvents, uiPreferences.currency, i18n.language, t]);

  const fireProjectionData = useMemo(() => generateProjectionData({
      basePortfolioItems: activeItems,
      liquidAssets,
      liquidAssetsInterestRate,
      projectionYears: dashboardSettings.fireHorizonYears || 40,
      dashboardSettings,
      timelineEvents: dashboardSettings.isScenarioActive ? timelineEvents : [],
      currency: uiPreferences.currency,
      locale: i18n.language,
      nowLabel: t('general.now')
  }), [activeItems, liquidAssets, liquidAssetsInterestRate, dashboardSettings, timelineEvents, uiPreferences.currency, i18n.language, t]);

  const fireSummary = useMemo(() => computeFireMetrics(fireProjectionData, {
      withdrawalRate: dashboardSettings.fireWithdrawalRate ?? 0.04,
      incomeT0Annual: totalAnnualIncome,
      coastTargetYear: dashboardSettings.fireCoastTargetYear,
      baseDate: new Date(),
  }), [fireProjectionData, dashboardSettings.fireWithdrawalRate, dashboardSettings.fireCoastTargetYear, totalAnnualIncome]);

  const sankeyData = useMemo(() => generateSankeyData({
      activeItems, liquidAssets, liquidAssetsInterestRate
  }), [activeItems, liquidAssets, liquidAssetsInterestRate]);
  
  const globalLoading = (loading || timelineLoading || snapshotsLoading) && !isDemoMode;

  // The boot screen stays over the shell until the first page has mounted.
  // Without it, entering the app (demo or sign-in) showed the nav and top bar
  // assembling around an inline spinner while the page chunk loaded. Reset on
  // the way back to the landing page so the next entry is covered too.
  const [isFirstPageReady, setIsFirstPageReady] = useState(false);
  const markFirstPageReady = useCallback(() => setIsFirstPageReady(true), []);
  const isInShell = !!currentUser || isDemoMode;
  useEffect(() => { if (!isInShell) setIsFirstPageReady(false); }, [isInShell]);

  const snapshotLabel = currentSnapshotId && activeSnapshot ? (activeSnapshot.name || t('snapshots.unnamed', 'Snapshot')) : t('snapshots.now', 'Now');

  const handleOpenSnapshotModal = useCallback(() => setIsSnapshotModalOpen(true), []);

  // F28.1 — the review page offers a snapshot once values have been confirmed:
  // that is the moment the portfolio is most worth freezing, because every
  // number in it was just checked. A second snapshot on the same day records
  // nothing new, so the offer stands down once today is already saved.
  const hasSnapshotToday = useMemo(() => snapshots.some((snapshot) => {
      const date = getEffectiveSnapshotDate(snapshot);
      return date
          && date.getFullYear() === evaluatedAt.getFullYear()
          && date.getMonth() === evaluatedAt.getMonth()
          && date.getDate() === evaluatedAt.getDate();
  }), [snapshots, evaluatedAt]);
  const handleCloseSnapshotModal = useCallback(() => setIsSnapshotModalOpen(false), []);

  const handleCreateSnapshot = useCallback((dateValue, nameValue = null) => {
      createSnapshot({
          portfolioItems: items,
          timelineEvents,
          liquidAssets,
          liquidAssetsInterestRate,
          date: dateValue,
          locale: i18n.language,
          name: nameValue
      });
  }, [createSnapshot, items, timelineEvents, liquidAssets, liquidAssetsInterestRate, i18n.language]);

  const handleSelectSnapshot = useCallback((snapshotId) => {
      setCurrentSnapshotId(snapshotId);
      setIsSnapshotModalOpen(false);
  }, []);

  const handleDeleteSnapshot = useCallback((snapshotId) => {
      const snap = snapshots.find(s => s.id === snapshotId);
      const date = snap && getEffectiveSnapshotDate(snap);
      const name = (snap?.name && snap.name.trim()) || (date ? date.toLocaleDateString(i18n.language || undefined, { dateStyle: 'medium' }) : '');
      showModal(
          name ? t('snapshots.deleteConfirmNamed', { name }) : t('snapshots.deleteConfirm', 'Delete this snapshot?'),
          true, () => deleteSnapshot(snapshotId), null, null, 'general.delete', 'general.cancel', true
      );
  }, [deleteSnapshot, showModal, t, snapshots, i18n.language]);

  const handleDuplicateSnapshot = useCallback((snap) => {
      if (!snap) return;
      const effectiveDate = getEffectiveSnapshotDate(snap) || new Date();

      const displayName = (typeof snap.name === 'string' && snap.name.trim()) ? snap.name.trim() : (() => {
          try {
              return effectiveDate.toLocaleDateString(i18n.language || undefined, { dateStyle: 'medium' });
          } catch (_) { return ''; }
      })() || t('snapshots.unnamed', 'Snapshot');
      const newName = t('snapshots.copyOfName', 'Copy of {{name}}', { name: displayName });
      createSnapshot({
          portfolioItems: snap.portfolioItems || [],
          timelineEvents: snap.timelineEvents || [],
          liquidAssets: snap.liquidAssets ?? 0,
          liquidAssetsInterestRate: snap.liquidAssetsInterestRate ?? 0,
          date: effectiveDate,
          locale: i18n.language,
          name: newName,
      });
  }, [createSnapshot, i18n.language, t]);

  const handleViewNow = useCallback(() => {
      setCurrentSnapshotId(null);
      setIsSnapshotModalOpen(false);
  }, []);

  // F27 — Settings actions
  const handleReplayOnboarding = useCallback(() => {
      handleUpdateDashboardSettings({ onboardingStatus: 'pending', onboardingStep: 0 });
  }, [handleUpdateDashboardSettings]);

  const handleResetAccount = useCallback(() => {
      const keyword = t('settings.danger.resetKeyword');
      showModal(
          t('settings.danger.resetConfirmTitle'),
          true,
          (values) => {
              if ((values.resetConfirmText || '').trim().toUpperCase() !== keyword.toUpperCase()) {
                  // stay open with the typed text, and say what is wrong under it
                  setModalError(t('settings.danger.resetKeywordMismatch', { keyword }));
                  return false;
              }
              resetAccountData(db, appId, userId).then(() => {
                  setCurrentSnapshotId(null);
                  setCurrentPage('dashboard');
              }).catch((error) => {
                  console.error('Account reset failed:', error);
                  showModal(errorMessage(t, error));
              });
          },
          [{ type: 'text', name: 'resetConfirmText', label: t('settings.danger.resetConfirmLabel', { keyword }), placeholder: keyword }],
          null,
          'settings.danger.resetButton',
          'general.cancel',
          true
      );
  }, [showModal, setModalError, t, userId]);

  // L2 — account deletion. The privacy policy has promised this since before
  // the paywall shipped; `handleResetAccount` above is a different thing (it
  // keeps the account and the email address) and was never a substitute.
  //
  // Everything destructive happens server-side in the `deleteAccount` callable,
  // which cancels Stripe first, then the data, then the auth user. This is only
  // the confirmation flow and the re-auth retry.
  const handleDeleteAccount = useCallback(() => {
      const keyword = t('settings.danger.deleteKeyword');
      showModal(
          t('settings.danger.deleteConfirmTitle'),
          true,
          (values) => {
              if ((values.deleteConfirmText || '').trim().toUpperCase() !== keyword.toUpperCase()) {
                  setModalError(t('settings.danger.deleteKeywordMismatch', { keyword }));
                  return false;
              }
              (async () => { try {
                  await httpsCallable(functions, 'deleteAccount')();
                  // The auth user is gone, so onAuthStateChanged fires on its
                  // own and the app falls back to the landing page. Signing out
                  // explicitly only makes that immediate and tidy.
                  setTimeout(() => showModal(t('settings.danger.deleteDone')), 0);
                  signOutLogic(showModal);
              } catch (error) {
                  console.error('deleteAccount failed:', error);
                  // The callable refuses on a stale session. Nothing was
                  // deleted, so the honest thing is to say why and let them
                  // sign in again rather than retry silently.
                  const needsReauth = error?.message === 'reauth-required'
                      || error?.code === 'functions/failed-precondition';
                  setTimeout(() => showModal(
                      needsReauth
                          ? t('settings.danger.deleteReauthNeeded')
                          : t('settings.danger.deleteFailed', { message: error?.message || '' })
                  ), 0);
              } })();
          },
          [{ type: 'text', name: 'deleteConfirmText', label: t('settings.danger.deleteConfirmLabel', { keyword }), placeholder: keyword }],
          // The paid-year forfeit is stated on the dialog that does the
          // deleting, not only in the Terms — this is the last moment it can
          // change someone's mind.
          isPro ? <p className="note">{t('settings.danger.deleteProWarning')}</p> : null,
          'settings.danger.deleteButton',
          'general.cancel',
          true
      );
  }, [showModal, setModalError, t, isPro, signOutLogic]);

  // F26 — onboarding lifecycle
  const handleOnboardingComplete = useCallback((status = 'completed') => {
      handleUpdateDashboardSettings({ onboardingStatus: status, onboardingCompletedAt: new Date() });
      setCurrentPage('dashboard');
  }, [handleUpdateDashboardSettings]);

  const handleOnboardingStepChange = useCallback((stepIndex) => {
      handleUpdateDashboardSettings({ onboardingStep: stepIndex });
  }, [handleUpdateDashboardSettings]);
  
  if (!isAuthReady && !isDemoMode) { return <LoadingSpinner text={t('general.loading')} />; }
  // The global modal is built here, ABOVE the early returns, and rendered by
  // each of them — not once at the bottom of the signed-in tree.
  //
  // Why: `showModal` only sets state. If the render path then changes to one
  // that does not include the modal, the state sits there unseen until some
  // later render happens to include it. Account deletion hit exactly that
  // (2026-09-06): it raised "your account and all its data have been deleted",
  // signed out in the same tick, and the app switched to the landing branch,
  // which returned before the modal ever rendered. The message then ambushed the
  // user on their next sign-in or demo session, describing a deletion from
  // minutes earlier.
  //
  // Anything raised while signed out was invisible the same way — this is the
  // class of bug, not the one instance.
  // plain, not useMemo: this runs after the early return above
  const currencySymbol = (() => {
      try {
          return new Intl.NumberFormat(i18n.language, { style: 'currency', currency: uiPreferences.currency })
              .formatToParts(0).find(p => p.type === 'currency')?.value || uiPreferences.currency;
      } catch (_) { return uiPreferences.currency; }
  })();

  // Always mounted: closing only flips isOpen, so the drawer slides out with
  // its content still in it instead of vanishing.
  const globalModal = (
            <ModalDrawer
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                title={typeof modalConfig.message === 'string' ? t(modalConfig.message) : modalConfig.message}
                theme={theme}
                drawerId="globalModal"
                footer={
                    <>
                        {/* a message with nothing to decide gets one button, and it is "OK" */}
                        <button onClick={closeModal} className={`btn ${modalConfig.isConfirm ? 'btn-ghost' : 'btn-grad'}`}>
                            {t(modalConfig.isConfirm ? (modalConfig.cancelLabel || 'general.cancel') : 'general.ok')}
                        </button>
                        {modalConfig.isConfirm && (
                            <button onClick={confirmAction} className={`btn ${modalConfig.danger ? 'btn-danger' : 'btn-grad'}`}>
                                {t(modalConfig.confirmLabel || 'general.confirm')}
                            </button>
                        )}
                    </>
                }
            >
                {modalConfig.children}
                {modalConfig.inputs && modalConfig.inputs.map((inputOpt, index) => (
                    <div className="form-group" key={inputOpt.name}>
                        <label className="form-label" htmlFor={inputOpt.name}>{inputOpt.label}</label>
                        {inputOpt.type === 'checkbox' ? (
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    id={inputOpt.name}
                                    name={inputOpt.name}
                                    checked={modalInputValues[inputOpt.name] || false}
                                    onChange={handleModalInputChange}
                                />
                                <span>{inputOpt.label}</span>
                            </label>
                        ) : (
                            <>
                                {inputOpt.type === 'percentage' || inputOpt.type === 'number' ? (
                                    <div className="input-with-suffix">
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            id={inputOpt.name}
                                            name={inputOpt.name}
                                            value={modalInputValues[inputOpt.name] || ''}
                                            onChange={handleModalInputChange}
                                            className={`form-input ${modalConfig.error ? 'error' : ''}`}
                                            placeholder={inputOpt.placeholder}
                                            data-autofocus={index === 0 ? '' : undefined}
                                        />
                                        <span className="input-suffix">{inputOpt.type === 'percentage' ? '%' : currencySymbol}</span>
                                    </div>
                                ) : (
                                    <input
                                        type={inputOpt.type}
                                        id={inputOpt.name}
                                        name={inputOpt.name}
                                        value={modalInputValues[inputOpt.name] || ''}
                                        onChange={handleModalInputChange}
                                        className={`form-input ${modalConfig.error ? 'error' : ''}`}
                                        placeholder={inputOpt.placeholder}
                                        data-autofocus={index === 0 ? '' : undefined}
                                    />
                                )}
                                {inputOpt.type === 'percentage' && (
                                    <span className="field-hint">
                                        {t('modal.percentageHint', 'Enter as a percentage (e.g., 5 for 5%).')}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                ))}
                {modalConfig.error && <p className="field-error" role="alert">{modalConfig.error}</p>}
            </ModalDrawer>
        );

  if (!currentUser && !isDemoMode) {
    // Community sub-landing pages live on dedicated paths (e.g. /fi for the
    // financial-independence community); everything else gets the main landing.
    const landingPath = window.location.pathname.replace(/\/+$/, '');
    const landingProps = { theme, onDemoMode: handleDemoMode, toggleTheme, autoOpenAuthMode: landingAuthMode };
    const landing = landingPath === '/fi'
        ? <FireLandingPage {...landingProps} />
        : (landingPath === '/planner' || landingPath === '/planovac')
            ? <PlannerLandingPage {...landingProps} />
            : <LandingPage {...landingProps} />;
    // `globalModal` rides along so a message raised on the way out — account
    // deletion is the one that matters — is actually seen.
    return <>{landing}{globalModal}</>;
  }
  if (globalLoading) { return <LoadingSpinner text={t('general.loading')} />; }

  // F26 — full-screen onboarding takeover for fresh sign-ups (and Settings
  // replays). 'pending' only ever originates from the sign-up flow or
  // Settings, so legacy users and demo mode never land here.
  if (!isDemoMode && currentUser && dashboardSettings.onboardingStatus === 'pending') {
      return (
          <>
          <OnboardingFlow
              theme={theme}
              isMobile={isMobile}
              items={items}
              handleAddItem={handleAddItem}
              handleDeleteItem={handleDeleteItem}
              liquidAssets={liquidAssets}
              liquidAssetsInterestRate={liquidAssetsInterestRate}
              onUpdateLiquidAssets={handleUpdateLiquidAssets}
              dashboardSettings={dashboardSettings}
              currency={uiPreferences.currency}
              onSetCurrency={uiPreferences.setCurrency}
              initialStep={dashboardSettings.onboardingStep || 0}
              onStepChange={handleOnboardingStepChange}
              onComplete={handleOnboardingComplete}
          />
          {globalModal}
          </>
      );
  }

  const renderCurrentPage = () => {
    switch(currentPage) {
        case 'dashboard':
            return <DashboardPage {...{items: activeItems, netWorth, grossWorth, totalMonthlyCashflow, totalAnnualAppreciation, projectionData, projectionYears, setProjectionYears, liquidAssets, liquidAssetsInterestRate, onEditLiquidAssets, effectivePortfolioYield, equityRatio, sankeyData, theme, dashboardSettings, onUpdateDashboardSettings: handleUpdateDashboardSettings, showModal, isMobile, onCustomizeDashboard: () => setIsCustomizeModalOpen(true), setCurrentPage: handleSetCurrentPage, totalAnnualIncome, uiPreferences, snapshots, currentSnapshotId, snapshotLabel, onOpenSnapshotModal: handleOpenSnapshotModal, onViewNow: handleViewNow, fireSummary, estimateCount: estimates.length, onOpenReview: openReviewPage, isReviewBannerDismissed, onDismissReviewBanner: () => setIsReviewBannerDismissed(true), awayDelta: showAwayDelta ? awayDelta : null, onDismissAwayBanner: () => setIsAwayBannerDismissed(true), isDemoMode }} />;
        case 'portfolio':
            return <PortfolioPage {...{items, navigateToAddItem, navigateToEditItem, handleDeleteItem, calcCashflow:itemMonthlyCashflow, calcAppreciation:calculateItemAnnualAppreciationValue, calcROE:calculateRealEstateReturnOnEquity, onItemActiveToggle: handleItemActiveToggle, handleBulkAction, ...uiPreferences, theme, isMobile, snapshotLabel, onOpenSnapshotModal: handleOpenSnapshotModal, currentSnapshotId, onViewNow: handleViewNow, estimatesByItemId, estimateCount: estimates.length, onOpenReview: openReviewPage}} />;
        case 'timeline':
            return <TimelinePage {...{theme, isMobile, showModal, userId, timelineEvents, portfolioItems: items, dashboardSettings, handleUpdateDashboardSettings, addTimelineEvent, updateTimelineEvent, deleteTimelineEvent, toggleTimelineEventDisabled, setCurrentPage: handleSetCurrentPage, uiPreferences, snapshotLabel, onOpenSnapshotModal: handleOpenSnapshotModal, currentSnapshotId, onViewNow: handleViewNow, onOpenPaywall: openPaywall}} />;
        case 'simulations':
            return <SimulationPage {...{theme, portfolioItems: items, timelineEvents, liquidAssets, liquidAssetsInterestRate, uiPreferences, dashboardSettings, snapshotLabel, onOpenSnapshotModal: handleOpenSnapshotModal, currentSnapshotId, onViewNow: handleViewNow, isPro, onOpenPaywall: openPaywall}} />;
        case 'fire':
            return <FirePage {...{theme, isMobile, items: activeItems, liquidAssets, liquidAssetsInterestRate, fireProjectionData, fireSummary, dashboardSettings, onUpdateDashboardSettings: handleUpdateDashboardSettings, onItemFireToggle: handleItemFireToggle, uiPreferences, setCurrentPage: handleSetCurrentPage, snapshotLabel, onOpenSnapshotModal: handleOpenSnapshotModal, currentSnapshotId, onViewNow: handleViewNow}} />;
        case 'review':
            return <ReviewPage {...{reviewList, onApply: handleApplyValue, onConfirmValue: handleConfirmValue, snapshotCount: snapshots.length, hasSnapshotToday, isPro, onCreateSnapshot: handleOpenSnapshotModal, onOpenItem: navigateToEditItem, onOpenLiquidAssets: onEditLiquidAssets, setCurrentPage: handleSetCurrentPage, currency: uiPreferences.currency, theme, isDemoMode}} />;
        case 'plan':
            return <PlanPage {...{isPro, planReady, isDemoMode, currentUser, currentPeriodEnd, cancelAtPeriodEnd, cancelAt, priceInterval, discountCode, discountDuration, billingStatus, onManageBilling: handleManageBilling, onCreateAccount: handleCreateAccountFromDemo, setCurrentPage: handleSetCurrentPage}} />;
        case 'settings':
            return <SettingsPage {...{theme, toggleTheme, isDemoMode, onOpenLocalizationModal: () => setIsLocalizationModalOpen(true), onReplayOnboarding: handleReplayOnboarding, onResetAccount: handleResetAccount, onDeleteAccount: handleDeleteAccount, isPro, planReady, currentPeriodEnd, cancelAtPeriodEnd, cancelAt, priceInterval, discountCode, discountDuration, billingStatus, onOpenPaywall: openPaywall, onManageBilling: handleManageBilling, onOpenPlan: () => handleSetCurrentPage('plan'), dashboardSettings, onUpdateDashboardSettings: handleUpdateDashboardSettings, onSignOut: handleSignOut}} />;
        default:
            return <DashboardPage {...{items: activeItems, netWorth, grossWorth, totalMonthlyCashflow, totalAnnualAppreciation, projectionData, projectionYears, setProjectionYears, liquidAssets, liquidAssetsInterestRate, onEditLiquidAssets, effectivePortfolioYield, equityRatio, sankeyData, theme, dashboardSettings, onUpdateDashboardSettings: handleUpdateDashboardSettings, showModal, isMobile, onCustomizeDashboard: () => setIsCustomizeModalOpen(true), setCurrentPage: handleSetCurrentPage, totalAnnualIncome, uiPreferences, snapshots, currentSnapshotId, snapshotLabel, onOpenSnapshotModal: handleOpenSnapshotModal, onViewNow: handleViewNow, fireSummary, estimateCount: estimates.length, onOpenReview: openReviewPage, isReviewBannerDismissed, onDismissReviewBanner: () => setIsReviewBannerDismissed(true), awayDelta: showAwayDelta ? awayDelta : null, onDismissAwayBanner: () => setIsAwayBannerDismissed(true), isDemoMode }} />;
    }
  }

  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<LoadingSpinner text={t('general.loading')} />}>
      <ExplainProvider dashboardSettings={dashboardSettings} onUpdateDashboardSettings={handleUpdateDashboardSettings}>
      <div className="app-root">

        {/* Outside every page container and never re-rendered, so navigating
            cannot make the ground blink. */}
        <AppBackground />
        
        {isLocalizationModalOpen && <LocalizationModal isOpen={isLocalizationModalOpen} onClose={() => setIsLocalizationModalOpen(false)} theme={theme} uiPreferences={uiPreferences} />}
        {isBugReportModalOpen && <BugReportModal isOpen={isBugReportModalOpen} onClose={() => setIsBugReportModalOpen(false)} theme={theme} userId={userId} isPro={isPro} isDemoMode={isDemoMode} currentPage={currentPage} />}
        {/* Kept mounted and toggled via isOpen so ModalDrawer's slide-out close
            animation can play; a conditionally-mounted modal unmounts instantly
            and skips it. */}
        <SnapshotModal
                isOpen={isSnapshotModalOpen}
                onClose={handleCloseSnapshotModal}
                snapshots={snapshots}
                onCreateSnapshot={handleCreateSnapshot}
                onSelectSnapshot={handleSelectSnapshot}
                onDuplicateSnapshot={handleDuplicateSnapshot}
                onDeleteSnapshot={handleDeleteSnapshot}
                onUpdateSnapshotDate={(id, dateStr, name) => {
                    if (!id || !dateStr) return;
                    const parsed = parseDateInputToLocalDate(dateStr);
                    if (!parsed || isNaN(parsed.getTime())) return;
                    const payload = { snapshotDate: parsed };
                    if (typeof name === 'string') {
                        const trimmed = name.trim();
                        // Always include name (even if empty after trim) so user can clear it.
                        payload.name = trimmed;
                    }
                    updateSnapshot(id, payload);
                }}
                onViewNow={handleViewNow}
                currentSnapshotId={currentSnapshotId}
                theme={theme}
                locale={i18n.language}
                t={t}
                isPro={isPro}
                freeLimit={FREE_LIMITS.snapshots}
                onUpgrade={() => openPaywall('snapshots')}
            />

        {/* Freemium paywall — kept mounted (isOpen toggles) for the slide-out
            close animation, same as SnapshotModal above. */}
        <PaywallModal
            isOpen={paywallSource !== null}
            onClose={closePaywall}
            source={paywallSource}
            theme={theme}
            isDemoMode={isDemoMode}
            currentUser={currentUser}
            onCreateAccount={handleCreateAccountFromDemo}
            onOpenPlan={() => handleSetCurrentPage('plan')}
        />

        {/* Post-checkout: "activating…" until the webhook flips the plan,
            then the success moment. */}
        {checkoutReturn && currentUser && !isDemoMode && (
            <ModalDrawer
                isOpen={true}
                onClose={() => setCheckoutReturn(false)}
                title={isPro ? t('billing.checkout.successTitle') : t('billing.checkout.activatingTitle')}
                theme={theme}
                drawerId="checkoutReturn"
                footer={isPro ? (
                    <button className="btn btn-grad" onClick={() => setCheckoutReturn(false)}>
                        {t('billing.checkout.successCta')}
                    </button>
                ) : null}
            >
                {/* The webhook lands a second or two after the redirect, so
                    there is a real gap where the payment is done and the plan is
                    not. It gets a state with words rather than a bare spinner. */}
                {isPro ? (
                    <>
                        <div className="co-ico ok"><CheckCircle2 size={26} /></div>
                        <p className="note">{t('billing.checkout.successBody')}</p>
                    </>
                ) : (
                    <>
                        <div className="co-ico"><span className="spinner lg" /></div>
                        <p className="note">{t('billing.checkout.activatingBody')}</p>
                        <p className="note" style={{ marginTop: 10 }}>{t('billing.checkout.activatingHint')}</p>
                    </>
                )}
            </ModalDrawer>
        )}

        {globalModal}
        
        {isItemFormOpen && (
            <ModalDrawer
                isOpen={isItemFormOpen}
                onClose={handleCancelForm}
                title={editingItem ? t('itemForm.editItemTitle') : t('itemForm.addItemTitle')}
                theme={theme}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <button type="button" onClick={handleCancelForm} className="btn btn-ghost">{t('general.cancel')}</button>
                        <button type="button" onClick={() => itemFormRef.current?.submit()} className="btn btn-grad" disabled={isSavingItem} aria-busy={isSavingItem}>
                            {isSavingItem && <span className="spinner" aria-hidden="true" />}
                            {editingItem ? t('general.save') : t('general.add')}
                        </button>
                    </div>
                }
            >
                <ItemForm 
                    ref={itemFormRef}
                    initialData={editingItem} 
                    onSubmit={(item) => {
                        if (isSavingItem) return;
                        setIsSavingItem(true);
                        const save = editingItem ? handleUpdateItem : handleAddItem;
                        Promise.resolve(save(item, { onSuccess: handleCancelForm }))
                            .finally(() => setIsSavingItem(false));
                    }} 
                    onCancel={handleCancelForm} 
                    showModal={showModal} 
                    theme={theme} 
                    isMobile={isMobile}
                    isInsideModal={true}
                    currency={uiPreferences.currency}
                />
            </ModalDrawer>
        )}

        <DashboardCustomizationModal
              isOpen={isCustomizeModalOpen}
              onClose={() => setIsCustomizeModalOpen(false)}
              widgetVisibility={dashboardSettings.widgetVisibility}
              onVisibilityChange={(widgetKey, isVisible) => handleUpdateDashboardSettings({ widgetVisibility: { ...dashboardSettings.widgetVisibility, [widgetKey]: isVisible }})}
              cardDensity={dashboardSettings.cardDensity}
              onDensityChange={(cardDensity) => handleUpdateDashboardSettings({ cardDensity })}
              theme={theme}
        />

        {isShellMobile && (
          <SideDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onOpenLocalizationModal={() => setIsLocalizationModalOpen(true)}
            onOpenBugReport={() => setIsBugReportModalOpen(true)}
            theme={theme}
            toggleTheme={toggleTheme}
            onSignOut={handleSignOut}
            isDemoMode={isDemoMode}
            currentUser={currentUser}
            setCurrentPage={handleSetCurrentPage}
            currentPage={currentPage}
            isPro={isPro}
            planReady={planReady}
            estimateCount={estimates.length}
          />
        )}


        {/* The grid: sidebar column, then everything else. The shell is mounted
            once and only the page inside it changes, so navigating never
            re-creates the frame or makes the background blink. */}
        <div className="app">
          {!isShellMobile && (
            <SideDrawer
              isOpen={true}
              onClose={() => {}}
              onOpenLocalizationModal={() => setIsLocalizationModalOpen(true)}
              onOpenBugReport={() => setIsBugReportModalOpen(true)}
              theme={theme}
              toggleTheme={toggleTheme}
              onSignOut={handleSignOut}
              isDemoMode={isDemoMode}
              currentUser={currentUser}
              setCurrentPage={handleSetCurrentPage}
              currentPage={currentPage}
              isPro={isPro}
              planReady={planReady}
              estimateCount={estimates.length}
            />
          )}
          <div className="col">
            {isShellMobile && (
              <MobileTopBar />
            )}
            {/* Annotation pass 4: on a phone the banner is a second bar of
                chrome above the first card, so its button moves into the top bar
                and the bar itself goes. Above 900px there is room for both. */}
            {/* Annotation pass 4 dropped this below 900px — on a phone it was a
                second bar of chrome above the first card for one sentence and
                one button — and moved the button into MobileTopBar instead.
                D2 brings it back on every width, because the sentence stopped
                being the least of it: now that demo edits actually stick, the
                banner is the one always-visible place that says they are not
                saved, and the top bar's button carried no such warning. The
                mobile rule in primitives.css already hides `.sub` and keeps the
                bold line, so the phone gets exactly the warning and the CTA. */}
            {isDemoMode && <DemoBanner onCreateAccount={handleCreateAccountFromDemo} />}
            <main className="main">
              <Suspense fallback={isFirstPageReady ? <PageFallback text={t('general.loading')} /> : null}>
                {renderCurrentPage()}
                {!isFirstPageReady && <PageReady onReady={markFirstPageReady} />}
              </Suspense>
              {/* One footer at the root, which is what the demo's own note says
                  the app should carry — it sits inside .main so it scrolls with
                  the page and lines up with its content column. */}
              <AppFooter
                onOpenPlan={() => handleSetCurrentPage('plan')}
                onOpenBugReport={() => setIsBugReportModalOpen(true)}
                canReportBug={!isDemoMode && !!currentUser}
              />
            </main>
          </div>
        </div>

        {isShellMobile && (
          <BottomNav currentPage={currentPage} setCurrentPage={handleSetCurrentPage} onMenuClick={() => setIsDrawerOpen(true)} theme={theme} estimateCount={estimates.length} />
        )}

        <Toast message={t('general.pressBackAgainToExit')} visible={exitToastVisible} />

        {/* Same look as LoadingSpinner, kept mounted so dropping `open` fades it out. */}
        <div className={`boot${isFirstPageReady ? '' : ' open'}`} aria-hidden={isFirstPageReady}>
            <AppLogo />
            <span className="spinner lg" />
            <p>{t('general.loading')}</p>
        </div>
      </div>
      </ExplainProvider>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

export default App;